import { Component, input, Input, OnInit } from "@angular/core";
import { RADController } from "app/_controllers/radController";
import { IAction } from "app/_models/IAction";
import { IActionGroup } from "app/_models/IActionGroup";
import { ISelectedLine } from "app/_models/ISelectedLine";
import { IDashboard } from "app/_models/IDashboard";
import { IDashboardGroup } from "app/_models/IDashboardGroup";
import { IGanttTask } from "app/_models/IGanttTask";
import { IHeader } from "app/_models/IHeader";
import { IReport } from "app/_models/IReport";
import { IReportGroup } from "app/_models/IReportGroup";
import { ISettings } from "app/_models/ISettings";
import { ISourceViewHeader } from "app/_models/ISourceViewHeader";
import { ITab } from "app/_models/ITab";
import { CommunicationService } from "app/_services/communication.service";
import { AppComponent } from "app/app.component";
import { IconDefinition as IconDefinitionSolid, faStar as faStarSolid } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition as IconDefinitionRegular, faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';

@Component({
  selector: 'app-rad',
  templateUrl: './rad.component.html',
  styleUrls: ['./rad.component.css']
})
export class RadComponent implements OnInit {
  //#region Fields
  @Input() settings: ISettings;
  @Input() userId: string;
  @Input() nodeId: string;
  @Input() mobileView: boolean;
  actionGroups: IActionGroup[] = [];
  reportGroups: IReportGroup[] = [];
  dashboardGroups: IDashboardGroup[] = [];
  selectedItems: ISelectedLine[] | IGanttTask[] = [];
  tabs: ITab[] = [];
  headers: IHeader[] | ISourceViewHeader[] = [];
  filterButtons: boolean;
  viewName: string;
  serverDateFormat: string = '';
  favouritedActions: { action: IAction, groupName: string }[] = [];
  faStarRegular: IconDefinitionRegular = faStarRegular;
  faStarSolid: IconDefinitionSolid = faStarSolid;
  //#endregion

  constructor(
    public app: AppComponent,
    private radController: RADController,
    private communicationService: CommunicationService
  ) {
    this.communicationService.grid_or_gantt_rad_loadRad$.subscribe(data => {
      this.tabs = [];
      this.selectedItems = [];
      this.filterButtons = true;
      this.viewName = data.viewName;
      this.nodeId = data.nodeId;
      this.favouritedActions = [];
      this.loadActions();
    })

    this.communicationService.gantt_or_detailgrid_rad_updateHeaders$.subscribe(data => {
      this.headers = data.headers;
      this.serverDateFormat = data.serverDateFormat;
    })

    this.communicationService.grids_or_gantt_rad_filterRadButtons$.subscribe(data => {
      this.selectedItems = data.selectedItems;
      this.viewName = data.currentview;
      if (this.actionGroups.length > 0 || this.reportGroups.length > 0 || this.dashboardGroups.length > 0) {
        this.filterRadButtons(this.viewName);
      }
    })

    this.communicationService.base_rad_collapseRadTabs$.subscribe(() => {
      this.collapseTabs();
    })
  }

  ngOnInit(): void { }

  loadActions(): void {
    this.actionGroups = [];
    this.radController.getActions(this.nodeId, this.userId).then(actionGroups => {
      if (actionGroups) {
        this.actionGroups = actionGroups;
                
        if (this.viewName.toLowerCase().includes('gantt')) {
          for (let i = 0; i < this.actionGroups.length; i++) {
            let actions = this.actionGroups[i].value;                        
            let action = actions.find(a => a.name.toLowerCase() == 'edit');
            if (action) sessionStorage.setItem('qx_ganttUpdateAction', JSON.stringify(action));           
          }
        }        
        
        if (localStorage.getItem(`qx_favActions_${this.nodeId}`)) {
          let savedFavouritedActions = JSON.parse(localStorage.getItem(`qx_favActions_${this.nodeId}`));

          this.favouritedActions = [];
          if (savedFavouritedActions && savedFavouritedActions.length > 0) {
            for (let i = 0; i < this.actionGroups.length; i++) {
              let actions = this.actionGroups[i].value;
              for (let j = 0; j < actions.length; j++) {
                let action = actions[j];
                let fav = savedFavouritedActions.find(p => p.action.name == action.name && p.groupName == this.actionGroups[i].key);
                if (fav) {
                  action.favourited = true;
                  this.favouritedActions.push({
                    action: action,
                    groupName: this.actionGroups[i].key
                  });
                } 
              }
            }
          }
        }
      }

      if (this.favouritedActions.length > 0)
        localStorage.setItem(`qx_favActions_${this.nodeId}`, JSON.stringify(this.favouritedActions));
      this.loadReports();
    });
  }

  loadAction(action: IAction): void {
    if (action.executePerLine && this.selectedItems.length == 0) {
      this.app.alertError(this.app.translations.ALERT_Error_SelectNoLines);
      return;
    }

    if (action.parameters != null) {
      action.parameters.forEach(param => {
        param.preselectedValue = '';
        param.value = '';
        param.lookupValues = [];
      })
    }
            
    if (action.type != 'SqlExecuteAction') {
      this.radController.checkIdRequirements(this.nodeId, action.id).then((data) => {
        sessionStorage.setItem('qx_action_idcheck', JSON.stringify(data));
      });
    }
        
    this.communicationService.showActionDialog(this.headers, action, this.selectedItems, this.serverDateFormat);
  }

  loadReports(): void {
    this.reportGroups = [];
    this.radController.getReports(this.nodeId, this.userId).then((reportGroups) => {
      if (reportGroups.length > 0) this.reportGroups = reportGroups;      
      this.loadDashboards();      
    });
  }

  loadReport(report: IReport): void {
    this.collapseTabs();
    if (report.executePerLine) {
      if (this.selectedItems.length != 1) {
        this.app.alertError(this.app.translations.ALERT_Error_OnlySelectOneLine);
        return;
      }
    }

    localStorage.setItem('qx_report', JSON.stringify({ reportId: report.id.toString(), lineId: this.selectedItems.length == 1 ? this.selectedItems[0].id : undefined }));
    window.open('report-viewer', '_blank');
  }

  loadDashboards(): void {
    this.dashboardGroups = [];
    this.radController.getDashboards(this.nodeId, this.userId).then((dashboardGroups) => {
      if (dashboardGroups.length > 0) this.dashboardGroups = dashboardGroups;
      this.loadTabs();
    });
  }

  loadDashboard(dashboard: IDashboard): void {
    this.collapseTabs();
    localStorage.setItem('qx_dashboard', JSON.stringify({ dashboardId: dashboard.id, refreshInterval: dashboard.refreshInterval }));
    window.open('dashboard-viewer', '_blank');
  }

  filterRadButtons(currentViewName: string): void {
    this.actionGroups = this.radController.filterRadButtons(this.actionGroups, currentViewName, this.selectedItems);
    this.reportGroups = this.radController.filterRadButtons(this.reportGroups, currentViewName, this.selectedItems);
    this.dashboardGroups = this.radController.filterRadButtons(this.dashboardGroups, currentViewName, this.selectedItems);
  }

  loadTabs(): void {    
    let index = 0;
    if (this.actionGroups.length > 0)
      this.tabs.push({ index: index++, groupName: this.app.translations.GBL_lbl_ActionsText, data: this.actionGroups, selected: false });

    if (this.reportGroups.length > 0)
      this.tabs.push({ index: index++, groupName: this.app.translations.GBL_lbl_ReportText, data: this.reportGroups, selected: false });

    if (this.dashboardGroups.length > 0)
      this.tabs.push({ index: index, groupName: this.app.translations.GBL_lbl_DashboardsText, data: this.dashboardGroups, selected: false });

    if (sessionStorage.getItem('filterDirectView')) {
      let viewname = sessionStorage.getItem('filterDirectView').split('|')[0];
      this.selectedItems = JSON.parse(sessionStorage.getItem('filterDirectView').split('|')[1]);
      this.filterRadButtons(viewname);
      sessionStorage.removeItem('filterDirectView');
      return;
    }

    if (this.filterButtons) {
      this.filterRadButtons(this.viewName);
      this.filterButtons = false;
    }
  }

  changeTab(tabChanged: ITab): void {    
    this.tabs.forEach(tab => {
      if (tabChanged != tab) {
        tab.selected = false;
        tab.data.forEach(tabGroup => {
          tabGroup.expanded = false;
        })
      }
    })

    tabChanged.selected = !tabChanged.selected;
    this.communicationService.adjustZIndex(tabChanged.selected);
  }

  expandGroup<T extends (IActionGroup | IReportGroup | IDashboardGroup)>(group: T): void {
    this.tabs.forEach(tab => {
      tab.data.forEach(tabGroup => {
        tabGroup.expanded = false;
      })
    })

    group.expanded = !group.expanded;
  }

  collapseTabs(): void {
    this.tabs.forEach(tab => { tab.selected = false; })
  }

  favouriteAction(action: IAction, actionGroup: IActionGroup): void {
    if (this.favouritedActions && this.favouritedActions.length == 9) {
      this.app.alertError('Max 9 actions can be favourited');
      return;
    }

    action.favourited = true;
    this.favouritedActions.push({
      action: action,
      groupName: actionGroup.key
    });

    localStorage.setItem(`qx_favActions_${this.nodeId}`, JSON.stringify(this.favouritedActions));
  }

  unfavouriteAction(favAction: { action: IAction, groupName: string }, action: IAction, groupName: string): void {
    let favIndex = -1;
    if (favAction) {
      favAction.action.favourited = false;
      favIndex = this.favouritedActions.findIndex(f => f.action.name == favAction.action.name && f.groupName == favAction.groupName);      
    } else {
      action.favourited = false;
      favIndex = this.favouritedActions.findIndex(f => f.action.name == action.name && f.groupName == groupName);
    }

    if (favIndex !== -1) {
      this.favouritedActions.splice(favIndex, 1);
      if (this.favouritedActions.length > 0)
        localStorage.setItem(`qx_favActions_${this.nodeId}`, JSON.stringify(this.favouritedActions));
      else
        localStorage.removeItem(`qx_favActions_${this.nodeId}`);      
    }  
  }
}